import { Exception, InternalFailure, ValidationException } from '@src/types';
import { AppInfoLoader } from '@services/AppInfoLoader';
import { ErrorCodeDirectory } from '@src/utils/ErrorCode';
import * as toastr from 'toastr';

// A log tag that marks all messages that are shown to the user.
const MESSAGE_SERVICE_TAG = 'USR-MSG';

interface IMessage {
    msg: string;
    type: string;
    exception: Exception;
}

/*
 * A service that aggregates error messages for an Angular app.
 */
export class MessageUtils {
    private static messages: IMessage[] = [];

    public static addMessageWithType(msg, type, exception = null) {
        this.messages.push({
            msg,
            type,
            exception,
        });

        this.logMessage(type, msg);
    }

    public static addMessage(msg) {
        this.addMessageWithType(msg, 'info');
    }

    public static addSuccessMessage(msg) {
        this.addMessageWithType(msg, 'success');
    }

    public static addWarningMessage(msg) {
        this.addMessageWithType(msg, 'warning');
    }

    public static addErrorMessage(error, prefixMsg) {
        this.addFriendlyDangerMessage(this.makeErrorMessage(error, prefixMsg, {}), null);
    }

    public static addFriendlyDangerMessage(friendlyMsg, exception) {
        this.addMessageWithType(friendlyMsg, 'danger', exception);
    }

    public static clearMessages() {
        this.messages.length = 0;
    }

    public static clearMessageWithIndex(index) {
        if (index >= 0 && index < this.messages.length) {
            MessageUtils.messages.splice(index, 1);
        }
    }

    public static getAllMessages() {
        return [...this.messages];
    }

    /**
     * Use with utils/errorCodes.ts
     * If the error object has a registered error code, add a "danger" message
     * defined by the error code.
     */
    public static addDangerMessageFromErrorWithCode(error, prefixMsg, params, exception) {
        this.addFriendlyDangerMessage(this.makeErrorMessage(error, prefixMsg, params), exception);
    }

    /**
     * Use with utils/errorCodes.js
     * If the error object has a registered error code, add a "danger" toast
     * defined by the error code.
     */
    public static addDangerToastFromErrorWithCode(error, prefixMsg, params) {
        this.addDangerToast(this.makeErrorMessage(error, prefixMsg, params));
    }

    // jQuery toastr
    public static addSuccessToast(toast) {
        setTimeout(() => {
            toastr.success(toast);
        });
        this.logMessage('toast', toast);
    }

    // jQuery toastr
    public static addErrorToast(error, toast) {
        const msg = this.makeErrorMessage(error, toast, {});
        setTimeout(() => {
            toastr.error(msg);
        });
        this.logMessage('toast-error', msg);
    }

    // jQuery toastr
    public static addDangerToast(toast) {
        setTimeout(() => {
            toastr.error(toast);
        });
        this.logMessage('toast-danger', toast);
    }

    private static makeErrorMessage(error, prefixMsg, params) {
        let msg = prefixMsg;
        if (error === undefined) {
            msg += 'Unknown Error.';
        } else if (error.code && ErrorCodeDirectory.hasErrorCode(error.code)) {
            msg += ErrorCodeDirectory.getErrorByName(error.code).format(params);
        } else if (error instanceof ValidationException) {
            msg += 'Invalid Input.';
        } else if (error instanceof InternalFailure) {
            msg += 'Internal failure.';
        } else if (error instanceof Exception) {
            msg += error.getMessage();
        } else {
            msg += JSON.stringify(error);
        }
        return msg;
    }

    private static getToolName() {
        return AppInfoLoader.getAppConfig().then((configData) => {
            return configData.toolName;
        });
    }

    private static logMessage(type, msg) {
        this.getToolName().then((response) => {
            const message = `[${type}] ${MESSAGE_SERVICE_TAG}/${response}:${msg}`;
            console.info(message);
        });
    }
}
